import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AlertTriangle, ArrowLeft, Globe, Info, Loader2, Sparkles, Target, TrendingUp } from "lucide-react";

/**
 * AdminCoverageBenchmarks — admin-only breadth+depth coverage audit.
 *
 * Every 30s live-refreshes from Mongo, comparing our current counts
 * of universities + degrees per country against the canonical target
 * universes in `backend/data/coverage_benchmarks.py`. Surfaces the
 * "Recommended Next" country as a hero card so editors always know
 * the highest-leverage gap to close.
 */
export default function AdminCoverageBenchmarks() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [sortKey, setSortKey] = useState("gap");  // gap | uni | deg | depth | country

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const r = await api.get("/admin/coverage-benchmarks");
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Load failed");
      }
    }
    load();
    // Live audit — refresh every 30s so counts stay honest without
    // the editor manually reloading the page.
    const t = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const sortedRows = useMemo(() => {
    if (!data?.rows) return [];
    const rows = [...data.rows];
    const keyMap = {
      gap: (r) => -r.gap_score,
      uni: (r) => -r.universities_pct,
      deg: (r) => -r.degrees_pct,
      depth: (r) => -r.depth_pct,
      country: (r) => r.country,
    };
    return rows.sort((a, b) => {
      const va = keyMap[sortKey](a);
      const vb = keyMap[sortKey](b);
      if (typeof va === "string") return va.localeCompare(vb);
      return va - vb;
    });
  }, [data, sortKey]);

  if (err) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 text-rose-800" data-testid="coverage-benchmarks-error">
        {err}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-24 text-stone-500 font-mono text-sm" data-testid="coverage-benchmarks-loading">
        <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Auditing coverage…
      </div>
    );
  }

  const rec = data.recommended_next;
  return (
    <div className="max-w-6xl mx-auto px-6 py-10" data-testid="coverage-benchmarks-page">
      <Link
        to="/admin"
        className="text-sm text-stone-500 hover:text-[#4C1D95] inline-flex items-center gap-1.5"
        data-testid="coverage-benchmarks-back"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to Newsroom
      </Link>

      <div className="mt-4 flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-underground-sc tracking-widest text-stone-500">
            Editorial CMS · Coverage Audit
          </div>
          <h1 className="mt-1 font-editorial text-4xl text-[#002147]">
            Coverage Benchmarks
          </h1>
          <p className="mt-2 text-stone-600 text-sm max-w-2xl">
            Live audit of universities + degrees per country against the
            canonical "target universe" internationals actually consider.
            Benchmarks last reviewed{" "}
            <span className="font-mono">{data.benchmark_last_reviewed}</span>.
          </p>
        </div>
        <div className="text-[11px] font-underground-sc tracking-wider text-stone-500 text-right">
          <div data-testid="benchmarks-total-unis">
            {data.totals.universities_have.toLocaleString()} / {data.totals.universities_estimated.toLocaleString()} unis · {data.totals.universities_pct}%
          </div>
          <div className="mt-1" data-testid="benchmarks-total-degrees">
            {data.totals.degrees_have.toLocaleString()} / {data.totals.degrees_estimated.toLocaleString()} degrees · {data.totals.degrees_pct}%
          </div>
        </div>
      </div>

      {/* Recommended Next hero */}
      {rec && (
        <div
          data-testid="recommended-next-card"
          className="mt-6 rounded-xl border border-[#4C1D95]/30 bg-gradient-to-r from-[#F5F0FF] to-white p-6"
        >
          <div className="flex items-start gap-4">
            <div className="shrink-0 mt-1 rounded-full bg-[#4C1D95] text-white p-2.5">
              <Sparkles className="h-5 w-5" aria-hidden />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-underground-sc tracking-widest text-[#4C1D95]">
                Recommended Next · biggest weighted win
              </div>
              <h2
                data-testid="recommended-next-country"
                className="mt-1 font-editorial text-2xl text-[#002147]"
              >
                {rec.country}
              </h2>
              <p
                data-testid="recommended-next-action"
                className="mt-2 text-sm text-stone-700 leading-relaxed"
              >
                {rec.next_action}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <MetricPill label="Unis" have={rec.universities_have} est={rec.universities_estimated} pct={rec.universities_pct} />
                <MetricPill label="Degrees" have={rec.degrees_have} est={rec.degrees_estimated} pct={rec.degrees_pct} />
                <MetricPill label="Depth" pctOnly={rec.depth_pct} />
                <span className="inline-flex items-center gap-1 rounded-full bg-[#4C1D95] text-white text-[10px] font-underground-sc tracking-wider px-2 py-1">
                  <Target className="h-3 w-3" aria-hidden />
                  gap score {rec.gap_score}
                </span>
              </div>
              {rec.note && (
                <p className="mt-3 text-[11px] italic text-stone-500 leading-relaxed">
                  {rec.note}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Watchtower — countries approaching the 100% ceiling.
          Surfaces any country whose universities_pct OR degrees_pct is
          ≥90% so the editorial team can bump the benchmark before it
          silently flips over 100% (the exact drift that produced the
          US "179.5%" surprise in Feb 2026). Only renders when at
          least one country hits the threshold. */}
      {(() => {
        const alerts = (data.rows || [])
          .map((r) => {
            const uPct = r.universities_pct ?? 0;
            const dPct = r.degrees_pct ?? 0;
            const worst = Math.max(uPct, dPct);
            return { r, worst, uPct, dPct };
          })
          .filter(({ worst }) => worst >= 90)
          .sort((a, b) => b.worst - a.worst);
        if (!alerts.length) return null;
        return (
          <div
            data-testid="coverage-watchtower"
            className="mt-6 rounded-xl border border-amber-300 bg-amber-50/70 p-5"
          >
            <div className="flex items-start gap-4">
              <div className="shrink-0 mt-0.5 rounded-full bg-amber-500 text-white p-2">
                <AlertTriangle className="h-4 w-4" aria-hidden />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-[10px] font-underground-sc tracking-widest text-amber-800">
                      Coverage Watchtower · {alerts.length} at ≥90%
                    </div>
                    <h2 className="mt-1 font-editorial text-xl text-amber-900">
                      {alerts.length === 1
                        ? `${alerts[0].r.country} is closing in on its benchmark`
                        : "Several countries are closing in on their benchmarks"}
                    </h2>
                  </div>
                  <div className="text-[11px] font-mono text-amber-800">
                    Bump the target before it flips over 100%
                  </div>
                </div>
                <ul className="mt-3 flex flex-wrap gap-2" data-testid="coverage-watchtower-list">
                  {alerts.map(({ r, worst, uPct, dPct }) => (
                    <li
                      key={r.country}
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-mono ${
                        worst >= 100
                          ? "bg-rose-100 text-rose-800 border border-rose-300"
                          : "bg-amber-100 text-amber-900 border border-amber-300"
                      }`}
                      data-testid={`coverage-watchtower-${r.country.replace(/\s+/g, "-").toLowerCase()}`}
                      title={`Unis ${uPct}% · Degrees ${dPct}%`}
                    >
                      <span className="font-semibold">{r.country}</span>
                      <span>· uni {uPct}%</span>
                      <span>· deg {dPct}%</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Table */}
      <div className="mt-8">
        <div className="flex items-baseline gap-3 mb-3">
          <div className="label-uppercase text-stone-500">All countries</div>
          <div className="text-[10px] font-underground-sc tracking-wider text-stone-400">
            Auto-refreshes every 30s · click a column to re-sort
          </div>
        </div>
        <div className="overflow-x-auto rounded-lg border border-stone-200 bg-white">
          <table className="w-full text-sm" data-testid="benchmarks-table">
            <thead className="bg-stone-50 text-stone-600 text-[11px] font-underground-sc tracking-wider">
              <tr>
                <ThButton label="Country" active={sortKey === "country"} onClick={() => setSortKey("country")} testid="benchmarks-sort-country" />
                <ThButton label="Universities" active={sortKey === "uni"} onClick={() => setSortKey("uni")} testid="benchmarks-sort-uni" />
                <ThButton label="Degrees" active={sortKey === "deg"} onClick={() => setSortKey("deg")} testid="benchmarks-sort-deg" />
                <ThButton label="Depth" active={sortKey === "depth"} onClick={() => setSortKey("depth")} testid="benchmarks-sort-depth" />
                <th className="text-left px-3 py-2 font-normal" title="Only counts fields traced to a per-row official source (LEO5, Scorecard 5yr earnings, HESA typical-entry, per-uni tuition/deadline URLs). Indicative fills excluded.">
                  Verified
                </th>
                <ThButton label="Gap × weight" active={sortKey === "gap"} onClick={() => setSortKey("gap")} testid="benchmarks-sort-gap" />
                <th className="text-left px-3 py-2 font-normal">Next action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {sortedRows.map((r) => (
                <tr
                  key={r.country}
                  data-testid={`benchmarks-row-${r.country.replace(/\s+/g, "-").toLowerCase()}`}
                  className="hover:bg-stone-50"
                >
                  <td className="px-3 py-2.5 font-medium text-[#002147] whitespace-nowrap">
                    {r.country}
                    {r.weight !== 1.0 && (
                      <span className="ml-1.5 text-[9px] font-mono text-stone-400" title={`Strategic weight ${r.weight}`}>
                        ×{r.weight}
                      </span>
                    )}
                  </td>
                  <PctCell have={r.universities_have} est={r.universities_estimated} pct={r.universities_pct} />
                  <PctCell have={r.degrees_have} est={r.degrees_estimated} pct={r.degrees_pct} />
                  <DepthCell pct={r.depth_pct} />
                  <td
                    className="px-3 py-2.5 font-mono text-[11px] whitespace-nowrap text-emerald-800"
                    data-testid={`benchmarks-cell-verified-${r.country.replace(/\s+/g, "-").toLowerCase()}`}
                    title="Percentage of tracked fields with an official per-row source citation"
                  >
                    {(r.depth_pct_verified ?? 0).toFixed(1)}%
                  </td>
                  <td className="px-3 py-2.5 font-mono text-[11px] text-stone-700">
                    <span className="inline-flex items-center gap-1">
                      <TrendingUp className="h-3 w-3 text-[#4C1D95]" aria-hidden />
                      {r.gap_score}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-[11px] text-stone-600">
                    {r.next_action}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-8 p-5 rounded-xl border border-dashed border-stone-300 bg-stone-50/60">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-stone-600 mt-0.5" />
          <div className="text-xs text-stone-600 leading-relaxed max-w-3xl">
            <b className="text-stone-800">Methodology.</b> Target universes come from{" "}
            <span className="font-mono">backend/data/coverage_benchmarks.py</span> (edit that file to update).
            Coverage % = live Mongo count / target × 100 — values can exceed 100% when we've curated more
            programmes than the target implies (e.g. Portuguese-taught pt-* rows above the DGES English-taught count).
            Gap score = (100–uni%)·0.35 + (100–deg%)·0.35 + (100–depth%)·0.30, multiplied by the country's
            strategic weight. Highest score wins the "Recommended Next" slot.
          </div>
        </div>
      </div>
    </div>
  );
}


function ThButton({ label, active, onClick, testid }) {
  return (
    <th className="text-left px-3 py-2 font-normal">
      <button
        type="button"
        onClick={onClick}
        data-testid={testid}
        className={`inline-flex items-center gap-1 ${active ? "text-[#4C1D95] font-semibold" : "text-stone-600 hover:text-stone-900"}`}
      >
        {label}
      </button>
    </th>
  );
}


function PctCell({ have, est, pct }) {
  const tint =
    pct >= 80 ? "text-emerald-800 bg-emerald-50"
    : pct >= 40 ? "text-amber-800 bg-amber-50"
    : "text-rose-800 bg-rose-50";
  return (
    <td className="px-3 py-2.5 whitespace-nowrap">
      <div className="font-mono text-[11px] text-stone-500">{have.toLocaleString()} / {est.toLocaleString()}</div>
      <div className={`inline-flex items-center rounded-full ${tint} px-2 py-0.5 text-[11px] font-mono mt-0.5`}>{pct}%</div>
    </td>
  );
}


function DepthCell({ pct }) {
  const tint =
    pct >= 50 ? "text-emerald-800 bg-emerald-50"
    : pct >= 20 ? "text-amber-800 bg-amber-50"
    : "text-rose-800 bg-rose-50";
  return (
    <td className="px-3 py-2.5 whitespace-nowrap">
      <span className={`inline-flex items-center rounded-full ${tint} px-2 py-0.5 text-[11px] font-mono`}>{pct}%</span>
    </td>
  );
}


function MetricPill({ label, have, est, pct, pctOnly }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#4C1D95]/25 text-[#002147] text-[11px] font-mono px-2.5 py-1">
      <span className="uppercase tracking-wider text-[9px] text-stone-500">{label}</span>
      {pctOnly != null ? (
        <span>{pctOnly}%</span>
      ) : (
        <span>{have.toLocaleString()}/{est.toLocaleString()} · {pct}%</span>
      )}
    </span>
  );
}
